/**
 * Enhanced (Cosmos) visual preset — Three.js scene enhancements for the globe.
 *
 * Adds outer glow, inner rim light, animated star field, and cyan ambient
 * lighting when the "enhanced" visual preset is active.
 */

import * as THREE from 'three';
import type { GlobeInstance } from 'globe.gl';

// ── References for cleanup ──────────────────────────────────────────

let outerGlow: THREE.Mesh | null = null;
let innerGlow: THREE.Mesh | null = null;
let starField: THREE.Points | null = null;
let ambientLight: THREE.AmbientLight | null = null;
let animationFrameId: number | null = null;
let active = false;

// ── Public API ──────────────────────────────────────────────────────

/** Returns whether the enhanced visuals are currently applied. */
export function isEnhancedActive(): boolean {
  return active;
}

/**
 * Apply enhanced cosmos visuals to the globe scene.
 *
 * Creates an outer glow, inner rim glow, animated star field, and
 * cyan ambient light. Safe to call multiple times — removes previous
 * enhancements before reapplying.
 */
export function applyEnhancedVisuals(globe: GlobeInstance): void {
  // Prevent duplicate objects
  if (active) removeEnhancedVisuals(globe);

  const scene = globe.scene();

  // ── Outer glow ──────────────────────────────────────────────────
  const outerGeometry = new THREE.SphereGeometry(101, 64, 64);
  const outerMaterial = new THREE.MeshBasicMaterial({
    color: '#4488cc',
    transparent: true,
    opacity: 0.08,
    blending: THREE.AdditiveBlending,
    side: THREE.BackSide,
  });
  outerGlow = new THREE.Mesh(outerGeometry, outerMaterial);
  scene.add(outerGlow);

  // ── Inner glow (rim lighting) ───────────────────────────────────
  const innerGeometry = new THREE.SphereGeometry(100.5, 64, 64);
  const innerMaterial = new THREE.MeshBasicMaterial({
    color: '#66aaff',
    transparent: true,
    opacity: 0.12,
    blending: THREE.AdditiveBlending,
    side: THREE.FrontSide,
  });
  innerGlow = new THREE.Mesh(innerGeometry, innerMaterial);
  scene.add(innerGlow);

  // ── Star field ──────────────────────────────────────────────────
  const starCount = 2000;
  const positions = new Float32Array(starCount * 3);
  for (let i = 0; i < starCount; i++) {
    // Distribute uniformly within a sphere of radius 500
    const r = 500 * Math.cbrt(Math.random());
    const theta = Math.acos(2 * Math.random() - 1);
    const phi = 2 * Math.PI * Math.random();
    positions[i * 3] = r * Math.sin(theta) * Math.cos(phi);
    positions[i * 3 + 1] = r * Math.sin(theta) * Math.sin(phi);
    positions[i * 3 + 2] = r * Math.cos(theta);
  }
  const starGeometry = new THREE.BufferGeometry();
  starGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

  const starMaterial = new THREE.PointsMaterial({
    color: '#ffffff',
    size: 0.8,
    transparent: true,
    opacity: 0.7,
  });
  starField = new THREE.Points(starGeometry, starMaterial);
  scene.add(starField);

  // ── Cyan ambient light ──────────────────────────────────────────
  ambientLight = new THREE.AmbientLight('#224466', 0.3);
  scene.add(ambientLight);

  // ── Twinkle animation ───────────────────────────────────────────
  startTwinkle();

  active = true;
}

/**
 * Remove all enhanced visuals from the globe scene and dispose GPU resources.
 */
export function removeEnhancedVisuals(globe: GlobeInstance): void {
  if (!active) return;

  const scene = globe.scene();

  stopTwinkle();

  if (outerGlow) {
    scene.remove(outerGlow);
    outerGlow.geometry.dispose();
    (outerGlow.material as THREE.Material).dispose();
    outerGlow = null;
  }

  if (innerGlow) {
    scene.remove(innerGlow);
    innerGlow.geometry.dispose();
    (innerGlow.material as THREE.Material).dispose();
    innerGlow = null;
  }

  if (starField) {
    scene.remove(starField);
    starField.geometry.dispose();
    (starField.material as THREE.Material).dispose();
    starField = null;
  }

  if (ambientLight) {
    scene.remove(ambientLight);
    ambientLight.dispose();
    ambientLight = null;
  }

  active = false;
}

// ── Twinkle animation ─────────────────────────────────────────────

function startTwinkle(): void {
  function tick(): void {
    if (starField) {
      const material = starField.material as THREE.PointsMaterial;
      // Oscillate between 0.5 and 0.9
      material.opacity = 0.7 + 0.2 * Math.sin(Date.now() * 0.001);
    }
    animationFrameId = requestAnimationFrame(tick);
  }
  animationFrameId = requestAnimationFrame(tick);
}

function stopTwinkle(): void {
  if (animationFrameId !== null) {
    cancelAnimationFrame(animationFrameId);
    animationFrameId = null;
  }
}
